// import { eventService } from '@/services/event.service';
import { registerHttpRequest } from '@/services/api/http.service';
import { eventService } from '@/services/event.service';
import {
  createClipTag,
  createVideoClip,
  deleteClipTag,
  getClipTags,
  getVideoClips,
  updateClipTag,
  updateVideoClip,
} from '@/services/video-clip.service';
import CameraService from '@/services/CameraService';
import store from '@/store';
import { mapVideoClip } from '@/utils/helpers';

const state = {
  videoClips: [],
  videoClip: null,
  clipEvents: [],
  clipTags: [],
  clipList: [],
  clipListLoading: false,
  lowLatencyModeById: [],
  searchParams: null,
};

// Getter functions
const getters = {};

// Mutations
const mutations = {
  setVideoClips(state, videoClips) {
    state.videoClips = videoClips;
  },
  addVideoClips(state, videoClips) {
    state.videoClips.push(...videoClips);
  },
  setVideoClip(state, videoClip) {
    state.videoClip = videoClip;
  },
  updateVideoClip(state, videoClip) {
    const index = state.videoClips.findIndex(
      (item) => item.id === videoClip.id
    );
    if (index >= 0) {
      const currentVideoClip = state.videoClips[index];
      currentVideoClip.clipName = videoClip.clipName;
      if (videoClip.tags && videoClip.tags[0]) {
        currentVideoClip.tags = [];
        currentVideoClip.tags.push(
          state.clipTags.find((tag) => tag.id === videoClip.tags[0])
        );
      } else {
        currentVideoClip.tags = null; // case of moving to no-folder
      }
      state.videoClip = { ...state.videoClip, ...currentVideoClip };
      state.videoClips.splice(index, 1, { ...currentVideoClip });
    }
  },
  setClipEvents(state, clipEvents) {
    state.clipEvents = clipEvents;
  },
  setClipTags(state, clipTags) {
    state.clipTags = clipTags;
  },
  setClipList(state, clipList) {
    state.clipList = clipList;
  },
  updateTag(state, tag) {
    const index = state.clipTags.findIndex((item) => item.id === tag.id);
    state.clipTags.splice(index, 1, { ...tag });
  },
  removeClipTag(state, tagId) {
    const index = state.clipTags.findIndex((item) => item.id === tagId);
    state.clipTags.splice(index, 1);
  },
  setClipListLoading(state, status) {
    state.clipListLoading = status;
  },
  setLowLatencyModeById(state, payload) {
    if (state.lowLatencyModeById && state.lowLatencyModeById.length) {
      const existingId = state.lowLatencyModeById.findIndex(
        (item) => item.id === payload.id
      );
      if (existingId > -1) {
        state.lowLatencyModeById[existingId].latencyMode = payload.latencyMode;
      } else {
        state.lowLatencyModeById.push(payload);
      }
    } else {
      state.lowLatencyModeById.push(payload);
    }
  },
  setSearchParams(state, payload) {
    state.searchParams = payload;
  },
};

// Actions
const actions = {
  async createVideoClip(_, { data }) {
    const $videoClips = data.map((cam) => {
      const mapCam = { ...cam, tags: [] };

      return createVideoClip(mapCam);
    });
    await Promise.all($videoClips);
  },
  async fetchVideoClips({ commit, dispatch, rootGetters, state }, payload) {
    const storedClipSearchParams =
      state.searchParams || CameraService.getClipSearchParams();
    if (storedClipSearchParams) {
      /**
       * This part keeps search results from clip filter
       * That can navigate to one of them normally
       */
      const init = payload.init;
      const currentOffset = payload.offset;
      payload = storedClipSearchParams;
      const clipListPosition = CameraService.getClipListPosition();
      if (init) {
        // Get stored offset for refreshing
        // Keep all previous items in the list
        payload.offset = 0;
        payload.upperLimit = clipListPosition < 1 ? 50 : clipListPosition + 50;
      } else {
        // Get stored offset for continue loading
        payload.upperLimit = 50;
        let storedOffset = storedClipSearchParams.offset;
        if (currentOffset >= storedOffset) {
          payload.offset = currentOffset;
        }
      }
      dispatch('setSearchParams', payload);
    }

    const videoClips = await getVideoClips(payload);
    if (videoClips._isCancel) {
      return { hasMore: false };
    }

    // filter list based on selected camera group
    const currentGroupCamList = rootGetters['common/currentGroupCamList'] || [];
    const filteredVideoClips = currentGroupCamList.length
      ? videoClips.list.filter((clip) =>
          currentGroupCamList.some((groupCam) => clip.cameraId === groupCam.id)
        )
      : videoClips.list;

    const mapVideoClips = filteredVideoClips.map(mapVideoClip(payload));
    commit(
      'addVideoClips',
      mapVideoClips.slice(
        Math.max(mapVideoClips.length - payload.upperLimit, 0)
      )
    );
    return { hasMore: !!videoClips.isNextData, total: videoClips.totalCount };
  },
  async fetchVideoClipsToTargetClip({ commit, rootGetters }, payload) {
    let offset = 0;
    const limit = 50;
    let hasMore = true;
    let total = 0;
    const searchParams = {
      upperLimit: limit,
      offset,
      tagIds: [],
    };
    let mapVideoClips = [];
    while (!mapVideoClips.find((c) => +c.id === +payload.clipId) && hasMore) {
      searchParams.offset = offset;

      const videoClips = await getVideoClips(searchParams);
      if (videoClips._isCancel) {
        return { hasMore: false };
      }

      // filter list based on selected camera group
      const currentGroupCamList =
        rootGetters['common/currentGroupCamList'] || [];
      const filteredVideoClips = currentGroupCamList.length
        ? videoClips.list.filter((clip) =>
            currentGroupCamList.some(
              (groupCam) => clip.cameraId === groupCam.id
            )
          )
        : videoClips.list;

      mapVideoClips = filteredVideoClips.map(mapVideoClip(payload));
      commit(
        'addVideoClips',
        mapVideoClips.slice(
          Math.max(mapVideoClips.length - payload.upperLimit, 0)
        )
      );
      hasMore = !!videoClips.isNextData;
      total = videoClips.totalCount;
      offset = offset + limit;
    }
    const targetClip = mapVideoClips.find((c) => +c.id === +payload.clipId);
    return { hasMore, total, targetClip, offset, limit };
  },
  removeClipById({ state, commit }, clipId) {
    const clipList = state.videoClips.filter((item) => item.id !== clipId);
    commit('setVideoClips', clipList);
  },
  resetSearchClips({ commit }) {
    commit('setVideoClips', []);
  },
  changeVideoClip({ commit }, data) {
    commit('setVideoClip', data);
  },

  async updateVideoClip({ commit }, data) {
    await updateVideoClip(data);
    commit('updateVideoClip', {
      id: data.id,
      clipName: data.name,
      tags: data.tagIds,
    });
  },
  async fetchClipEvents({ commit }, data) {
    const events = await eventService.getEventList({
      cameraIds: [data.cameraId],
      startDate: data.start,
      endDate: data.end,
    });
    commit('setClipEvents', events.list || []);
  },
  async fetchClipTags({ commit }) {
    const params = {};
    registerHttpRequest('getClipTags', params);

    const result = await getClipTags(params);
    commit('setClipTags', result.tags || []);
    return result.tags;
  },
  setClipList({ commit }, payload) {
    commit('setClipList', payload);
  },
  setClipListLoading({ commit }, status) {
    commit('setClipListLoading', status);
  },
  async createTag({ dispatch }, payload) {
    const { tag, clips } = payload;
    const result = await createClipTag(tag);
    await Promise.all(
      clips.map((clip) =>
        dispatch('updateVideoClip', {
          id: clip.id,
          name: clip.clipName,
          tagIds: [result.id],
        })
      )
    );
  },
  async updateTag({ commit }, payload) {
    const result = await updateClipTag(payload);
    commit('updateTag', result);
  },
  async moveToTag({ dispatch }, payload) {
    const { tagId, clips } = payload;
    await Promise.all(
      clips.map((clip) => {
        const data = {
          id: clip.id,
          name: clip.clipName,
        };
        if (tagId) {
          data.tagIds = [tagId];
        }
        return dispatch('updateVideoClip', data);
      })
    );
  },
  async checkEmptyTagToDelete({ commit }, payload) {
    const searchParams = {
      upperLimit: 2,
      offset: 0,
      tagIds: [payload.tagId],
    };
    const videoClips = await getVideoClips(searchParams);
    if (videoClips.list.length === 0) {
      await deleteClipTag({ ids: [payload.tagId] });
      commit('removeClipTag', payload.tagId);
      return true;
    }
    return false;
  },
  async deleteTag({ commit }, payload) {
    await deleteClipTag(payload);
    payload.ids.forEach((tagId) => commit('removeClipTag', tagId));
  },
  setLowLatencyModeById({ commit }, payload) {
    commit('setLowLatencyModeById', payload);
    store.dispatch('common/updateCameraLatencyMode', payload).then();
  },
  setSearchParams({ commit }, payload) {
    CameraService.saveClipSearchParams(payload);
    commit('setSearchParams', payload);
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
